import { iono } from 'global';
import Component from './component';
import assets from '../assets';
import { delegate, enableAttribute, resizeObserver } from '../../utils/dom';
import { updateTooltip } from '../utils/tooltip';
import { qualityFormattedUsage } from '../../utils/quality';
import { supportedSyndicators } from '../../utils/subscribe';
import filesize from 'filesize';

const qualityIcons = [
  assets.icons.qualityLow,
  assets.icons.qualityMedium,
  assets.icons.qualityHigh
];

export default class PlaybackControls extends Component {
  constructor (props) {
    super(props);

    this.locale = this.props.locale;
    this.player = this.props.player;

    this.onactionclicked = delegate('[data-action]', this.onactionclicked.bind(this));
    this.onresize = this.onresize.bind(this);
    this.onseekable = this.onseekable.bind(this);
    this.onlevelschange = this.onlevelschange.bind(this);
    this.onsourcechange = this.onsourcechange.bind(this);
    this.onplaylistchange = this.onplaylistchange.bind(this);
    this.onplaylistselect = this.onplaylistselect.bind(this);
    this.onratechange = this.onratechange.bind(this);
    this.onvolumechange = this.onvolumechange.bind(this);
  }

  getDefaultProps () {
    return {
      seekSkipAmount: 15,
      syndicators: [],
      download: null,
      controls: {
        skipBackward: true,
        skipForward: true,
        volume: {
          volume: 100,
          volumes: [0, 30, 60, 100]
        },
        playbackSpeed: {
          speed: 1,
          speeds: [0.75, 1, 1.25, 1.5]
        },
        sharing: false,
        subscribe: false,
        quality: true,
        menu: true
      }
    };
  }

  attach (element) {
    super.attach(element);

    resizeObserver.observe(this.$element);

    this.$element.addEventListener('click', this.onactionclicked);
    this.$element.addEventListener('resize', this.onresize);

    this.player.on('seekable', this.onseekable);
    this.player.on('levelschange', this.onlevelschange);
    this.player.on('sourcechange', this.onsourcechange);
    this.player.on('playlistchange', this.onplaylistchange);
    this.player.on('playlistselect', this.onplaylistselect);
    this.player.on('ratechange', this.onratechange);
    this.player.on('volumechange', this.onvolumechange);
  }

  detach () {
    this.player.off('seekable', this.onseekable);
    this.player.off('levelschange', this.onlevelschange);
    this.player.off('sourcechange', this.onsourcechange);
    this.player.off('playlistchange', this.onplaylistchange);
    this.player.off('playlistselect', this.onplaylistselect);
    this.player.off('ratechange', this.onratechange);
    this.player.off('volumechange', this.onvolumechange);

    if (this.$element) {
      this.$element.removeEventListener('resize', this.onresize);
      this.$element.removeEventListener('click', this.onactionclicked);

      resizeObserver.unobserve(this.$element);
    }

    super.detach();
  }

  render () {
    const volume = this.player.getVolume();
    const playbackRate = this.player.getPlaybackRate();
    const hasQualityLevels = this.props.controls.quality && this.player.getQualityLevels().length > 1;
    const hasSyndicators = this.props.syndicators.length > 0;
    const hasDownload = this.props.source && this.props.source.download;
    const canSeek = this.player.isSeekable();
    const hasSeeking = !this.player.isLive();

    return `<div class="iono-playback-actions iono-button-group">
      ${this.props.controls.skipBackward ? `<button class="iono-button" data-action="skip-backward" ${hasSeeking ? '' : 'hidden'} ${canSeek ? '' : 'disabled'} title="⇠ 15s">${assets.icons.backward}</button>` : ''}
      ${this.props.controls.skipForward ? `<button class="iono-button" data-action="skip-forward" ${hasSeeking ? '' : 'hidden'} ${canSeek ? '' : 'disabled'} title="15s ⇢">${assets.icons.forward}</button>` : ''}
      
      ${this.props.controls.volume ? `<button class="iono-button" data-action="toggle-volume" ${iono.Player.isVolumeSupported() ? '' : 'hidden'} title="${this.locale.lang('player.volume')} – ${volume * 100}%">${this.getVolumeIcon(volume)}</button>` : ''}

      ${this.props.controls.playbackSpeed ? `<button class="iono-button" data-action="toggle-playback-speed" ${this.player.isLive() ? 'hidden' : ''} title="${this.locale.lang('player.playback_speed')} – ${playbackRate}×">${this.getPlaybackRateIcon(playbackRate)}</button>` : ''}
      ${this.props.controls.quality ? `<button class="iono-button" data-action="toggle-quality" ${hasQualityLevels ? '' : 'hidden'} title="${this.getQualityTooltip()}">${this.getQualityIcon(this.props.source)}</button>` : ''}
      ${this.props.controls.sharing ? `<button class="iono-button" data-action="show-sharing" title="${this.locale.lang('sharing.share')}">${assets.icons.share}</button>` : ''}
      ${this.props.controls.subscribe ? `<button class="iono-button" data-action="show-subscribe" ${hasSyndicators ? '' : 'hidden'} title="${this.locale.lang('player.subscribe')}">${assets.icons.subscribe}</button>` : ''}
      ${this.props.controls.download ? `<button class="iono-button" data-action="download" ${hasDownload ? '' : 'hidden'} title="${this.locale.lang('player.download')}">${assets.icons.download}</button>` : ''}
      ${this.props.controls.menu ? `<button class="iono-button" data-action="show-menu" title="${this.locale.lang('player.details')}">${assets.icons.chevronUp}</button>` : ''}
    </div>`;
  }

  update (changed = []) {
    if (this.props.controls.download && changed.includes('source')) {
      this.$('[data-action="download"]').setAttribute('title', `${this.locale.lang('player.download')} – ${filesize(this.props.source.filesize)}`);
    }
  }

  updateLevels () {
    const $level = this.$('.iono-button[data-action="toggle-quality"]');
    updateTooltip($level, { text: this.getQualityTooltip() });
    $level.innerHTML = this.getQualityIcon(this.props.source);
  }

  updatePlaybackRate () {
    const $playbackSpeed = this.$('.iono-button[data-action="toggle-playback-speed"]');
    const playbackSpeed = this.player.getPlaybackRate();

    updateTooltip($playbackSpeed, { text: `${this.locale.lang('player.playback_speed')} – ${playbackSpeed}×` });
    $playbackSpeed.innerHTML = this.getPlaybackRateIcon(playbackSpeed);
  }

  updateFeatures () {
    if (this.props.controls.quality) {
      const hasQualityLevels = this.player.getQualityLevels().length > 1;
      enableAttribute(this.$('[data-action="toggle-quality"]'), 'hidden', !hasQualityLevels);
    }

    if (this.props.controls.subscribe) {
      enableAttribute(this.$('[data-action="show-subscribe"]'), 'hidden', this.props.syndicators.length === 0);
    }

    const canSeek = this.player.isSeekable();
    const hasSeeking = !this.player.isLive();

    if (this.props.controls.skipBackward) {
      enableAttribute(this.$('[data-action="skip-backward"]'), 'hidden', !hasSeeking);
      enableAttribute(this.$('[data-action="skip-backward"]'), 'disabled', !canSeek);
    }

    if (this.props.controls.skipForward) {
      enableAttribute(this.$('[data-action="skip-forward"]'), 'hidden', !hasSeeking);
      enableAttribute(this.$('[data-action="skip-forward"]'), 'disabled', !canSeek);
    }

    if (this.props.controls.download) {
      const hasDownload = this.props.source && this.props.source.download;
      enableAttribute(this.$('[data-action="download"]'), 'hidden', !hasDownload);
    }

    if (this.props.controls.volume) {
      enableAttribute(this.$('[data-action="toggle-volume"]'), 'hidden', !iono.Player.isVolumeSupported());
    }

    if (this.props.controls.playbackSpeed) {
      enableAttribute(this.$('[data-action="toggle-playback-speed"]'), 'hidden', this.player.isLive());
    }
  }

  updateVolume () {
    if (this.props.controls.volume) {
      const $volume = this.$('[data-action="toggle-volume"]');
      const volume = this.player.getVolume();

      updateTooltip($volume, { text: `${this.locale.lang('player.volume')} – ${volume * 100}%` });
      $volume.innerHTML = this.getVolumeIcon(volume);
    }
  }

  getQualityTooltip () {
    if (!this.props.source) {
      return '';
    }

    const qualityUsage = qualityFormattedUsage(this.props.source, this.locale).toLowerCase();
    const qualityLabel = this.props.source.quality
      ? this.locale.lang(`player.${this.props.source.quality.toLowerCase()}`, this.props.source.quality)
      : '';

    return `${qualityLabel ? `${qualityLabel} – ` : ''}${qualityUsage}`;
  }

  getPlaybackRateIcon (rate) {
    if (rate < 1) {
      return assets.icons.speedSlow;
    } else if (rate >= 1.5) {
      return assets.icons.speedFastest;
    } else if (rate >= 1.25) {
      return assets.icons.speedFaster;
    }

    return assets.icons.speedNormal;
  }

  getVolumeIcon (volume) {
    if (volume === 1) {
      return assets.icons.volumeHigh;
    } else if (volume >= 0.6) {
      return assets.icons.volumeMedium;
    } else if (volume > 0) {
      return assets.icons.volumeLow;
    }

    return assets.icons.volumeOff;
  }

  getQualityIcon (quality) {
    if (!quality) return '';

    const qualityLevels = this.player.getQualityLevels()
      .slice()
      .sort((a, b) => { return a.bitrate - b.bitrate; });

    const lenLevels = qualityLevels.length;
    const lenIcons = qualityIcons.length;

    const min = Math.min(lenLevels, lenIcons);
    const max = Math.max(lenLevels, lenIcons);
    const mul = min / max;

    for (const [index, level] of qualityLevels.entries()) {
      if (level.bitrate === quality.bitrate) {
        return qualityIcons[Math.round(index * mul)];
      }
    }

    return qualityIcons[0];
  }

  toggleQuality () {
    const bitrate = this.player.getBitrate();
    const qualityLevel = this.player.getQualityLevel();
    const qualityLevels = this.player.getQualityLevels()
      .slice()
      .sort((a, b) => { return a.bitrate - b.bitrate; });

    for (const [index, level] of qualityLevels.entries()) {
      if (level.label === qualityLevel || level.bitrate === bitrate) {
        const nextQualityIndex = (index + 1 < qualityLevels.length ? qualityLevels[index + 1] : qualityLevels[0]).label;
        this.player.setQualityLevel(nextQualityIndex);
        return;
      }
    }
  }

  togglePlaybackSpeed () {
    const playbackRate = this.player.getPlaybackRate();
    const rates = this.props.controls.playbackSpeed.speeds;
    const currentIndex = rates.indexOf(playbackRate);
    this.player.setPlaybackRate(
      currentIndex + 1 < rates.length ? rates[currentIndex + 1] : rates[0]
    );
  }

  toggleVolume () {
    const volume = this.player.getVolume() * 100;
    const volumes = this.props.controls.volume.volumes;

    for (let i = 0; i < volumes.length; ++i) {
      const prev = volumes[i];
      const next = volumes[i + 1];
      const between = prev === volume || (prev <= volume && next > volume);

      if (between) {
        this.player.setVolume((next || volumes[0]) / 100);
        return;
      }
    }
  }

  triggerAction (action) {
    const event = new CustomEvent('action', { detail: { action } });

    this.$element.dispatchEvent(event);

    if (event.defaultPrevented) {
      return;
    }

    switch (action) {
      case 'toggle-playback-speed': this.togglePlaybackSpeed(); break;
      case 'toggle-quality': this.toggleQuality(); break;
      case 'toggle-volume': this.toggleVolume(); break;
      case 'download': this.player.download(); break;
      case 'skip-forward': this.player.setPosition(this.player.getPosition() + this.props.seekSkipAmount); break;
      case 'skip-backward': this.player.setPosition(this.player.getPosition() - this.props.seekSkipAmount); break;
      case 'retry': this.player.play(); break;
    }
  }

  onactionclicked (event) {
    this.triggerAction(event.delegateTarget.getAttribute('data-action'));
  }

  onresize (event) {
    this.updateFeatures();
  }

  onseekable (event) {
    this.updateFeatures();
  }

  onlevelschange (event) {
    this.updateLevels();
  }

  onsourcechange (event) {
    this.setProp('source', event.source);
    this.updateLevels();
    this.updateFeatures();
  }

  onplaylistchange (event) {
    if (event.loaded) {
      this.updateFeatures();
    }
  }

  onplaylistselect (event) {
    if (this.props.controls.subscribe > 0) {
      this.setProp('syndicators', supportedSyndicators(event.playlistItem.links || []), false);
      this.updateFeatures();
    }
  }

  onratechange (event) {
    this.updatePlaybackRate();
  }

  onvolumechange (event) {
    this.updateVolume();
  }
}
